/*
  EnquiryDictate — inline "record a phone message, get a pre-filled
  New Enquiry" panel used from EnquiryQuickForm.

  Flow
    1. User picks/tests their mic (MicPicker) — persisted deviceId is reused.
    2. Tap Record → MediaRecorder captures webm audio with a live timer.
    3. Tap Stop → upload the blob to POST /api/enquiries/transcribe.
    4. Backend replies with { transcript, extracted } → we show the
       transcript in a preview and call `onApply` with the extracted
       fields so the parent form can merge them in.

  We intentionally only overwrite empty parent fields on apply — never
  clobber values the therapist has already typed.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import MicPicker, { getSavedMicDeviceId } from "@/components/MicPicker";

const MAX_SECONDS = 5 * 60; // hard cap so users don't accidentally record an hour

function formatSecs(n) {
  const m = Math.floor(n / 60).toString().padStart(1, "0");
  const s = Math.floor(n % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function EnquiryDictate({ onApply, onClose }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [extracted, setExtracted] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);

  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const cleanupStream = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    streamRef.current = null;
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    cleanupStream();
  }, [cleanupStream]);

  async function start() {
    if (recording) return;
    setTranscript("");
    setExtracted(null);
    setAudioBlob(null);
    chunksRef.current = [];
    const deviceId = getSavedMicDeviceId();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        },
      });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      mediaRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setAudioBlob(blob);
        cleanupStream();
      };
      rec.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          const next = e + 1;
          if (next >= MAX_SECONDS) stop();
          return next;
        });
      }, 1000);
    } catch (e) {
      toast.error("Could not open microphone", {
        description: e?.name === "NotAllowedError"
          ? "Permission was denied. Enable mic access for this site and try again."
          : e?.message || String(e),
      });
    }
  }

  function stop() {
    if (!recording) return;
    try { mediaRef.current?.stop(); } catch { /* ignore */ }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  }

  async function upload() {
    if (!audioBlob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("audio", audioBlob, "enquiry.webm");
      const r = await api.post("/enquiries/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const t = r.data?.transcript || "";
      const ex = r.data?.extracted || {};
      setTranscript(t);
      setExtracted(ex);
      if (!t.trim()) {
        toast.warning("No speech detected in the recording");
      } else {
        toast.success("Transcript ready — review below and tap Apply");
      }
    } catch (e) {
      toast.error("Transcription failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setUploading(false);
    }
  }

  function reset() {
    setTranscript("");
    setExtracted(null);
    setAudioBlob(null);
    setElapsed(0);
    chunksRef.current = [];
  }

  function apply() {
    if (!transcript && !extracted) return;
    onApply?.({ transcript, extracted: extracted || {} });
  }

  const showExtractedRows = extracted && Object.keys(extracted).length > 0;

  return (
    <div className="border border-indigo-200 bg-indigo-50/40 rounded-md p-3 space-y-3" data-testid="enquiry-dictate-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-indigo-900 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Voice-to-Enquiry
          </div>
          <p className="text-2xs text-indigo-900/80 mt-0.5">
            Dictate the phone message — Whisper will transcribe and pre-fill the form.
            Anything the AI misses, you can type in as usual.
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-2xs text-indigo-900/70 hover:text-indigo-950 underline"
            data-testid="enquiry-dictate-close"
          >
            Hide
          </button>
        )}
      </div>

      <MicPicker disabled={recording || uploading} />

      <div className="flex items-center gap-3 flex-wrap">
        {!recording ? (
          <Button
            onClick={start}
            disabled={uploading}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="enquiry-dictate-record"
          >
            <Mic className="h-4 w-4 mr-1.5" />
            {audioBlob ? "Re-record" : "Record dictation"}
          </Button>
        ) : (
          <Button
            onClick={stop}
            className="bg-zinc-950 text-white hover:bg-zinc-800"
            data-testid="enquiry-dictate-stop"
          >
            <Square className="h-4 w-4 mr-1.5" /> Stop
          </Button>
        )}
        {(recording || elapsed > 0) && (
          <span
            className="text-sm font-mono-num tabular-nums text-zinc-800 inline-flex items-center gap-1.5"
            data-testid="enquiry-dictate-timer"
          >
            {recording && <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />}
            {formatSecs(elapsed)}
            {recording && <span className="text-2xs text-zinc-500">/ {formatSecs(MAX_SECONDS)}</span>}
          </span>
        )}
        {audioBlob && !recording && !transcript && (
          <Button
            onClick={upload}
            disabled={uploading}
            variant="outline"
            className="border-indigo-300 text-indigo-900 hover:bg-indigo-100"
            data-testid="enquiry-dictate-transcribe"
          >
            {uploading
              ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Transcribing…</>
              : <><Sparkles className="h-4 w-4 mr-1.5" /> Transcribe with Whisper</>}
          </Button>
        )}
        {(audioBlob || transcript) && !recording && !uploading && (
          <Button
            onClick={reset}
            variant="ghost"
            size="sm"
            className="text-zinc-600 hover:text-zinc-950"
            data-testid="enquiry-dictate-reset"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Discard
          </Button>
        )}
      </div>

      {transcript && (
        <div className="space-y-2" data-testid="enquiry-dictate-result">
          <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium">Transcript</div>
          <div className="rounded-md bg-white border border-zinc-200 p-2 text-sm text-zinc-800 whitespace-pre-wrap max-h-32 overflow-y-auto">
            {transcript}
          </div>
          {showExtractedRows && (
            <>
              <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium mt-2">
                Pre-filled fields
              </div>
              <ul className="rounded-md bg-white border border-zinc-200 divide-y divide-zinc-100 text-sm">
                {Object.entries(extracted).map(([k, v]) => (
                  <li key={k} className="px-2 py-1.5 flex items-start gap-2">
                    <span className="text-2xs uppercase tracking-wider text-zinc-500 min-w-[110px]">{k}</span>
                    <span className="text-zinc-800 flex-1">{String(v)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="flex justify-end">
            <Button
              onClick={apply}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="enquiry-dictate-apply"
            >
              <Sparkles className="h-4 w-4 mr-1.5" /> Apply to form
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
